/**
 * Per-store device version registry on the BackOffice service.
 *
 * Endpoints (live since 2026-10-01 — LNE-82):
 * - POST { backOfficeTomcatApi } check-device-version?DB={storeDB}
 *     body  : { device, app, version }  (all strings, no auth)
 *     reply : { device, app, current_version, latest_version, needs_update }
 *     side  : refreshes the device's `last_seen` on the store register
 *
 * - GET  { backOfficeTomcatApi } device-versions?DB={storeDB}&app={app?}
 *     reply : [{ device, app, version, last_seen, needs_update }]
 *     - `app` filter is exact-match; missing DB → 400 plain message;
 *       unknown store → []. Banner fallback for the login screen.
 *
 * Callers:
 * - src/views/UnifiedLoginView.vue (login screen only — LNE-83):
 *     check-in on store selection, drives the warning banner + Request Update
 *
 * This service throws on error so the caller can swallow. The login screen
 * never throws to the user; a failure here is identical to "no signal" and
 * the banner stays hidden.
 */
import axios from 'axios';

const POST_TIMEOUT_MS = 10000;
const GET_TIMEOUT_MS = 10000;

function apiBase() {
    if (typeof window === 'undefined' || !window.backOfficeTomcatApi) {
        throw new Error('deviceVersionService: window.backOfficeTomcatApi is not configured');
    }
    return window.backOfficeTomcatApi;
}

/**
 * App name string for this till.
 *
 * Matches the `platform` value sent to SubscriptionManager
 * `validatePCRegistration` (src/store/index.js:1279). The release register
 * groups devices per exact app name; using the same string keeps every
 * till in its own bucket regardless of platform form factor.
 */
export function getLocalAppName() {
    const isAndroid = typeof window !== 'undefined' && window.androidMode === true;
    return isAndroid ? 'Lytning Android RetailPOS' : 'Lytning Retail POS';
}

/**
 * Report this till's app version to the store register and get back whether
 * the till is behind the store's latest version of the same app.
 *
 * @param {string} storeDB - store database (e.g. "chilenje_store01").
 * @param {{ device: string, app: string, version: string }} payload
 * @returns {Promise<{ device: string, app: string, currentVersion: string, latestVersion: string, needsUpdate: boolean }>}
 */
export async function checkDeviceVersion(storeDB, payload) {
    if (!storeDB) {
        throw new Error('checkDeviceVersion: storeDB is required');
    }
    if (!payload || !payload.device || !payload.app || !payload.version) {
        throw new Error('checkDeviceVersion: device, app, version are all required');
    }
    const url = `${apiBase()}check-device-version?DB=${encodeURIComponent(storeDB)}`;
    const response = await axios.post(url, {
        device: String(payload.device),
        app: String(payload.app),
        version: String(payload.version),
    }, { timeout: POST_TIMEOUT_MS });
    const data = response && response.data ? response.data : {};
    return {
        device: data.device || payload.device,
        app: data.app || payload.app,
        currentVersion: data.current_version || payload.version,
        latestVersion: data.latest_version || payload.version,
        needsUpdate: Boolean(data.needs_update),
    };
}

/**
 * List the store's devices with their app versions and update status.
 *
 * @param {string} storeDB - store database.
 * @param {string} [app] - optional app name filter (exact match).
 * @returns {Promise<Array<{ device: string, app: string, version: string, lastSeen: string, needsUpdate: boolean }>>}
 */
export async function getDeviceVersions(storeDB, app) {
    if (!storeDB) {
        throw new Error('getDeviceVersions: storeDB is required');
    }
    const params = { DB: storeDB };
    if (app) params.app = app;
    const url = `${apiBase()}device-versions`;
    const response = await axios.get(url, { params, timeout: GET_TIMEOUT_MS });
    if (!Array.isArray(response.data)) return [];
    return response.data.map((row) => ({
        device: row.device || '',
        app: row.app || '',
        version: row.version || '',
        lastSeen: row.last_seen || '',
        needsUpdate: Boolean(row.needs_update),
    }));
}

export default {
    checkDeviceVersion,
    getDeviceVersions,
    getLocalAppName,
};
